'use client';

import React, { useRef, useState, useCallback } from 'react';
import { FiMoreVertical, FiMaximize2, FiDownload, FiFileText, FiShare2 } from 'react-icons/fi';
import domToImage from 'dom-to-image';

import type { TimeChartItem } from 'src/types/chart';
import dayjs from 'lib/date/dayjs';
import saveAsCSV from 'lib/saveAsCSV';

import ChartWidgetContent from './ChartWidgetContent';
import FullscreenChartModal from './FullscreenChartModal';
import useZoom from './hooks/useZoom';

import styles from './ChartWidget.module.css';

export type Props = {
  items?: Array<TimeChartItem>;
  title: string;
  description?: string;
  units?: string;
  isLoading: boolean;
  className?: string;
  isError: boolean;
  emptyText?: string;
  noAnimation?: boolean;
  href?: string;
  minH?: string;
  headerExtra?: React.ReactNode;
};

const ChartWidget = ({
  items,
  title,
  description,
  isLoading,
  className,
  isError,
  units,
  emptyText,
  noAnimation,
  href,
  headerExtra,
}: Props) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { zoomRange, handleZoom, handleZoomReset } = useZoom();
  const [ menuOpen, setMenuOpen ] = useState(false);
  const [ isFullscreen, setIsFullscreen ] = useState(false);

  const hasItems = items && items.length > 2;

  const handleFullscreen = useCallback(() => {
    setMenuOpen(false);
    setIsFullscreen(true);
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const handleSavePNG = useCallback(() => {
    setMenuOpen(false);
    setTimeout(() => {
      if (chartRef.current) {
        const scale = 5;
        const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
        domToImage.toPng(chartRef.current, {
          quality: 100,
          bgcolor: isDark ? '#000000' : '#ffffff',
          width: chartRef.current.offsetWidth * scale,
          height: chartRef.current.offsetHeight * scale,
          filter: (node: Node) => (node as Element).nodeName !== 'BUTTON',
          style: {
            borderColor: 'transparent',
            transform: `scale(${ scale })`,
            'transform-origin': 'top left',
          },
        }).then((dataUrl) => {
          const link = document.createElement('a');
          link.download = `${ title } (chart).png`;
          link.href = dataUrl;
          link.click();
          link.remove();
        });
      }
    }, 100);
  }, [ title ]);

  const handleSaveCSV = useCallback(() => {
    setMenuOpen(false);
    if (items) {
      const headerRows = [ 'Date', 'Value' ];
      const dataRows = items.map((item) => [
        dayjs(item.date).format('YYYY-MM-DD'), String(item.value),
      ]);
      saveAsCSV(headerRows, dataRows, `${ title } (stats)`);
    }
  }, [ items, title ]);

  const handleShare = useCallback(async() => {
    setMenuOpen(false);
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url: window.location.href,
        });
      } catch (e) {
        // user cancelled
      }
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(window.location.href);
    }
  }, [ title, description ]);

  const content = (
    <ChartWidgetContent
      items={ items }
      isError={ isError }
      isLoading={ isLoading }
      units={ units }
      title={ title }
      emptyText={ emptyText }
      handleZoom={ handleZoom }
      zoomRange={ zoomRange }
      noAnimation={ noAnimation }
    />
  );

  const hrefStr = typeof href === 'string' ? href : undefined;

  return (
    <>
      <div
        ref={ chartRef }
        className={ `${ styles.container } ${ className || '' }` }
      >
        <div className={ styles.header }>
          <div className={ styles.headerText }>
            { hrefStr ? (
              <a href={ hrefStr } className={ styles.title }>{ title }</a>
            ) : (
              <span className={ styles.title }>{ title }</span>
            ) }
            { description && (
              <span className={ styles.description }>{ description }</span>
            ) }
          </div>
          { headerExtra && <div className={ styles.headerExtra }>{ headerExtra }</div> }
          <div className={ styles.actions }>
            { zoomRange && (
              <button className={ styles.actionButton } onClick={ handleZoomReset } title="Reset zoom">
                ↺
              </button>
            ) }
            { !isLoading && (
              <div className={ styles.menuWrapper }>
                <button
                  className={ styles.actionButton }
                  onClick={ () => setMenuOpen(!menuOpen) }
                  title="Options"
                >
                  <FiMoreVertical size={ 16 }/>
                </button>
                { menuOpen && (
                  <>
                    <div className={ styles.menuBackdrop } onClick={ () => setMenuOpen(false) }/>
                    <div className={ styles.menu }>
                      <button className={ styles.menuItem } onClick={ handleFullscreen }>
                        <FiMaximize2 size={ 14 }/> View fullscreen
                      </button>
                      <button className={ styles.menuItem } onClick={ handleSavePNG }>
                        <FiDownload size={ 14 }/> Save as PNG
                      </button>
                      <button className={ styles.menuItem } onClick={ handleSaveCSV }>
                        <FiFileText size={ 14 }/> Save as CSV
                      </button>
                      <button className={ styles.menuItem } onClick={ handleShare }>
                        <FiShare2 size={ 14 }/> Share
                      </button>
                    </div>
                  </>
                ) }
              </div>
            ) }
          </div>
        </div>

        { content }
      </div>

      { hasItems && isFullscreen && (
        <FullscreenChartModal
          isOpen
          items={ items }
          title={ title }
          description={ description }
          onClose={ handleCloseFullscreen }
          units={ units }
          zoomRange={ zoomRange }
          handleZoom={ handleZoom }
          handleZoomReset={ handleZoomReset }
        />
      ) }
    </>
  );
};

export default React.memo(ChartWidget);
